import React, { Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/browser'
import Footer from '@fs/zion-footer'
import RebuggerClient from '@fs/zion-rebugger/AppClient'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { AppLayout } from '@fs/zion-ui'
import { Router } from '@fs/zion-router'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { NoticeLoading } from '@fs/zion-icon'
import { useFeatureFlag } from '@fs/zion-flags'

import App from './App'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import { RootProvider, useRoot } from './hooks'
import './locales'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

// add any app logic here, languages and translations are configured and available here
const FrontierWrapper = () => {
  const rewriteImageUrls = useFeatureFlag('chinese_memoryBooks_rewriteImageUrlsDev')?.isOn
  if (rewriteImageUrls) {
    import('./rewriteImageUrls')
  }

  return (
    <Router basename={base}>
      <Root analytics fullScreen>
        <AppLayout fullWidth>
          <RootProvider>
            <WrappedApp />
          </RootProvider>
        </AppLayout>
      </Root>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => (
  <React.StrictMode>
    <Suspense fallback={<NoticeLoading />}>
      <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
        <FrontierWrapper />
      </FrontierBaseProviders>
    </Suspense>
    {process.env.NODE_ENV === 'development' && <RebuggerClient />}
  </React.StrictMode>
)
function WrappedApp() {
  const { showFooter } = useRoot()

  return (
    <>
      <App />
      {showFooter && <Footer />}
    </>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister()
